import React, { useState, useEffect, useCallback, useRef } from 'react'
import { getSession } from '../../getSession'
import { getMailSmsDLTAccount, getFromMailId, getMailTemplate, getTemplateContentById, sendMassMailNew } from '../../utils/lead'
import Swal from 'sweetalert2'

const SendMail = ({
  onClose = () => {},
  onSend = (payload) => {},
  initialRecipients = { email1: true, email2: false, email3: false },
  selectedCount = 0,
  selectedLeadIds = [],
  hashedIds = '',
  massOpBatchId = '',
  applyOnAllLeads = false,
  // optional prop overrides for dropdowns
  senderOptions: propSenderOptions,
  emailOptions: propEmailOptions,
  templateOptions: propTemplateOptions,
  registerSubmitHandler,
}) => {
  const [recipients, setRecipients] = useState(initialRecipients)
  const [sender, setSender] = useState('')
  const [fromEmail, setFromEmail] = useState('')
  const [replyTo, setReplyTo] = useState('')
  const [mode, setMode] = useState('template') // 'template' or 'compose'
  const [template, setTemplate] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [schedule, setSchedule] = useState(false)
  const [scheduleAt, setScheduleAt] = useState('')
  const [error, setError] = useState('')

  const [senderOptions, setSenderOptions] = useState(propSenderOptions || [])
  const [emailOptions, setEmailOptions] = useState(propEmailOptions || [])
  const [templateList, setTemplateList] = useState(propTemplateOptions || [])
  const handleSendRef = useRef(null)

  useEffect(() => {
    if (propSenderOptions && propSenderOptions.length) {
      setSenderOptions(propSenderOptions)
      if (!sender) setSender(propSenderOptions[0])
    }
  }, [propSenderOptions])

  useEffect(() => {
    if (propEmailOptions && propEmailOptions.length) {
      setEmailOptions(propEmailOptions)
      if (!fromEmail) setFromEmail(propEmailOptions[0])
      if (!replyTo) setReplyTo(propEmailOptions[0])
    }
  }, [propEmailOptions])

  useEffect(() => {
    if (propTemplateOptions && propTemplateOptions.length) {
      setTemplateList(propTemplateOptions)
      if (!template) setTemplate(propTemplateOptions[0].value ?? propTemplateOptions[0].id ?? '')
    }
  }, [propTemplateOptions])

  useEffect(() => {
    let mounted = true

    const fetchTemplateContent = async () => {
      if (mode !== 'template' || !template) return

      try {
        const { userId, TokenId } = getSession()
        const payload = {
          Token: TokenId,
          Message: '',
          LoggedUserId: String(userId ?? ''),
          MAC_Address: '',
          IP_Address: '',
          Details: { TemplateID: String(template) },
          BroadcastName: ''
        }

        const resp = await getTemplateContentById(payload)
        const status = Number(resp?.Status ?? resp?.status ?? 0)
        if (!mounted || status !== 1) return

        const content = typeof resp?.Details === 'string'
          ? resp.Details
          : String(resp?.Details ?? '')

        setSubject(content)
      } catch (err) {
        console.warn('Failed to load mail template content:', err?.message || err)
      }
    }

    fetchTemplateContent()

    return () => {
      mounted = false
    }
  }, [template, mode])

  // fetch accounts (for sender) if not provided
  useEffect(() => {
    let mounted = true
    const fetchAccounts = async () => {
      if (propSenderOptions && propSenderOptions.length) return
      try {
        const { userId, TokenId, parentId } = getSession()
        const payload = {
          Token: TokenId,
          Message: '',
          LoggedUserId: userId,
          MAC_Address: '',
          IP_Address: '',
          Details: { ParentId: parentId, IsSms: false },
          BroadcastName: ''
        }
        const resp = await getMailSmsDLTAccount(payload)
        let list = resp?.Details ?? resp?.d ?? resp ?? []
        if (list && list.data) list = list.data
        if (list && !Array.isArray(list)) list = list.ResultList ?? list.Items ?? list.Data ?? list.Accounts ?? list
        const arr = Array.isArray(list) ? list.map(item => {
          if (!item) return ''
          if (typeof item === 'string') return item
          return item.AccountName || item.Name || item.Value || item.Text || item.DisplayName || JSON.stringify(item)
        }).filter(Boolean) : []
        if (mounted && arr.length) {
          setSenderOptions(['--Select Accounts--', ...arr])
          setSender(arr[0])
        }
      } catch (err) {
        console.warn('Failed to load mail accounts:', err?.message || err)
      }
    }
    fetchAccounts()
    return () => { mounted = false }
  }, [])

  // fetch from-email options
  useEffect(() => {
    let mounted = true
    const fetchFromEmails = async () => {
      if (propEmailOptions && propEmailOptions.length) return
      try {
        const { userId, TokenId, parentId } = getSession()
        const payload = {
          Token: TokenId,
          Message: '',
          LoggedUserId: userId,
          MAC_Address: '',
          IP_Address: '',
          Details: { ParentId: parentId ?? 0 },
          BroadcastName: ''
        }
        const list = await getFromMailId(payload)
        const options = Array.isArray(list) ? list.map(item => {
          if (!item) return null
          if (typeof item === 'string') return item
          const value = item.Code || item.Text || item.Email || item.FromMailId || item.MailId || item.Address || item.Name || (item.toString && item.toString()) || ''
          return value
        }).filter(Boolean) : []
        if (mounted && options.length) {
          setEmailOptions(options)
          setFromEmail(prev => prev || options[0])
          setReplyTo(prev => prev || options[0])
        }
      } catch (err) {
        console.warn('Failed to load from-mail ids:', err?.message || err)
      }
    }
    fetchFromEmails()
    return () => { mounted = false }
  }, [])

  // fetch templates
  useEffect(() => {
    let mounted = true
    const fetchTemplates = async () => {
      if (propTemplateOptions && propTemplateOptions.length) return
      try {
        const { userId, TokenId, parentId } = getSession()
        const payload = {
          Token: TokenId,
          Message: '',
          LoggedUserId: userId,
          MAC_Address: '',
          IP_Address: '',
          Details: { ParentID: parentId ?? 0, isSMS: false, isMail: true },
          BroadcastName: ''
        }
        const list = await getMailTemplate(payload)
        const opts = Array.isArray(list) ? list.map(item => {
          if (!item) return null
          if (typeof item === 'string') return { value: item, label: item }
          const value = item.MailTemplateId ?? item.MailTemplateID ?? item.Id ?? item.TemplateId ?? item.Code ?? item.Name ?? item.Title ?? item.Text ?? JSON.stringify(item)
          const label = item.MailTemplateName ?? item.MailTemplate ?? item.Name ?? item.TemplateName ?? item.Text ?? item.Title ?? item.Code ?? value
          return { value: String(value), label: String(label) }
        }).filter(Boolean) : []
        if (mounted && opts.length) {
          setTemplateList(opts)
          setTemplate(prev => prev || opts[0].value)
        }
      } catch (err) {
        console.warn('Failed to load mail templates:', err?.message || err)
      }
    }
    fetchTemplates()
    return () => { mounted = false }
  }, [])

  const toggleRecipient = (key) => setRecipients(prev => ({ ...prev, [key]: !prev[key] }))

  const extractAccountId = (accountValue) => {
    if (!accountValue) return 0
    if (typeof accountValue === 'number' && Number.isFinite(accountValue)) return accountValue

    const normalized = String(accountValue).trim()
    if (!normalized) return 0

    const directNumeric = Number(normalized)
    if (Number.isFinite(directNumeric) && directNumeric > 0) return directNumeric

    const allNumberParts = normalized.match(/\d+/g)
    if (!allNumberParts || !allNumberParts.length) return 0

    const parsed = Number(allNumberParts[allNumberParts.length - 1])
    return Number.isFinite(parsed) ? parsed : 0
  }

  const validate = (accountId, idsForPayload, shouldUseBatchId) => {
    setError('')
    if (!Object.values(recipients).some(Boolean)) {
      setError('Select at least one recipient (Email1/Email2/Email3).')
      return false
    }
    if (!idsForPayload && !shouldUseBatchId) {
      setError('No leads selected.')
      return false
    }
    if (accountId <= 0) {
      setError('Select Account')
      return false
    }
    if (!fromEmail) {
      setError('Select From MailID')
      return false
    }
    if (!subject.trim()) {
      setError('Subject is required.')
      return false
    }
    if (mode === 'template' && !template) {
      setError('Please select template !')
      return false
    }
    if (mode === 'compose' && !message.trim()) {
      setError('Provide mail to send')
      return false
    }
    if (schedule && !scheduleAt) {
      setError('Select schedule date and time.')
      return false
    }
    return true
  }

  const handleSend = useCallback(async () => {
    const accountId = extractAccountId(sender)
    const numericBatchId = Number(massOpBatchId) || 0
    const idsFromSelection = (hashedIds || (Array.isArray(selectedLeadIds) ? selectedLeadIds.join('/') : '') || '').trim()
    const shouldUseBatchId = !!applyOnAllLeads && numericBatchId > 0
    const idsForPayload = shouldUseBatchId ? '' : idsFromSelection

    if (!validate(accountId, idsForPayload, shouldUseBatchId)) return

    try {
      const { userId, TokenId } = getSession()
      const numericUserId = Number(userId) || 0

      const requestPayload = {
        Token: TokenId,
        Message: '',
        LoggedUserId: numericUserId,
        MAC_Address: '',
        IP_Address: '',
        Details: {
          massLeadMails: [
            {
              IDS: idsForPayload,
              AccountID: accountId,
              SenderName: sender || '',
              FromMailID: fromEmail || '',
              ReplyTo: replyTo || '',
              Subjects: subject || '',
              TemplateID: mode === 'template' ? (Number(template) || 0) : 0,
              ComposeMsg: mode === 'compose' ? (message || '') : '',
              EmailId: !!recipients.email1,
              EmailId1: !!recipients.email2,
              EmailId2: !!recipients.email3,
              Schedulemaildatetime: schedule ? (scheduleAt || '') : ''
            }
          ],
          IDS: idsForPayload,
          Userid: numericUserId,
          massbatchid: shouldUseBatchId ? numericBatchId : 0,
          Module: 'Lead',
          ModuleType: mode === 'compose' ? 'compose' : 'template',
          StartDateTime: schedule ? (scheduleAt || '') : ''
        },
        BroadcastName: ''
      }

      const resp = await sendMassMailNew(requestPayload)
      const status = Number(resp?.Status ?? resp?.status ?? 0)
      const details = resp?.Details
      const detailMessage = typeof details === 'string'
        ? details
        : (Array.isArray(details)
          ? (details[0]?.Result || details[0]?.Message || '')
          : (details?.Result || details?.Message || ''))

      if (status === 1) {
        Swal.fire({ icon: 'success', title: 'Sent', text: 'Mail sent successfully.' })
        onClose()
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: detailMessage || resp?.Message || 'Failed to send mail.' })
      }
    } catch (err) {
      console.error('send mass mail error', err)
      Swal.fire({ icon: 'error', title: 'Error', text: 'An error occurred while sending mail.' })
    }
  }, [
    sender,
    fromEmail,
    replyTo,
    mode,
    template,
    subject,
    message,
    recipients,
    schedule,
    scheduleAt,
    massOpBatchId,
    hashedIds,
    selectedLeadIds,
    applyOnAllLeads,
    onClose
  ])

  useEffect(() => {
    handleSendRef.current = handleSend
  }, [handleSend])

  useEffect(() => {
    if (typeof registerSubmitHandler === 'function') {
      registerSubmitHandler(() => () => handleSendRef.current?.())
      return () => registerSubmitHandler(null)
    }
  }, [registerSubmitHandler])

  return (
    <div className="w-full max-w-3xl">
      <div className="mb-3 text-sm text-blue-700 bg-blue-50 p-3 rounded">Info! You are about to perform a mass action on {selectedCount} record(s)</div>

      <div className="mb-4">
        <label className="inline-flex items-center mr-4">
          <input type="checkbox" checked={recipients.email1} onChange={() => toggleRecipient('email1')} className=" rounded-[3px] mr-2" />
          Email1
        </label>
        <label className="inline-flex items-center mr-4">
          <input type="checkbox" checked={recipients.email2} onChange={() => toggleRecipient('email2')} className=" rounded-[3px] mr-2" />
          Email2
        </label>
        <label className="inline-flex items-center">
          <input type="checkbox" checked={recipients.email3} onChange={() => toggleRecipient('email3')} className=" rounded-[3px] mr-2" />
          Email3
        </label>
      </div>

      <div className="space-y-3">
        <div>
          <select value={sender} onChange={e => setSender(e.target.value)} className="w-full border px-3 py-2 rounded">
            {senderOptions.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div>
          <select value={fromEmail} onChange={e => setFromEmail(e.target.value)} className="w-full border px-3 py-2 rounded mb-2">
            {emailOptions.map(ea => <option key={ea} value={ea}>{ea}</option>)}
          </select>

          <select value={replyTo} onChange={e => setReplyTo(e.target.value)} className="w-full border px-3 py-2 rounded">
            {emailOptions.map(ea => <option key={ea} value={ea}>{ea}</option>)}
          </select>
        </div>

        <div className="flex items-center gap-4">
          <label className="inline-flex items-center">
            <input type="radio" name="mail-mode" checked={mode === 'template'} onChange={() => setMode('template')} className="mr-2" />
            Select Template
          </label>
          <label className="inline-flex items-center">
            <input type="radio" name="mail-mode" checked={mode === 'compose'} onChange={() => setMode('compose')} className="mr-2" />
            Compose Mail
          </label>
        </div>

        <div>
          <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject" className="w-full border px-3 py-2 rounded" />
        </div>

        {mode === 'template' ? (
          <div>
            <div className="mb-2">
              <a href="#" className="text-blue-600 text-sm">Click To View</a>
            </div>
            <select value={template} onChange={e => setTemplate(e.target.value)} className="w-full border px-3 py-2 rounded">
              {templateList.map(t => {
                if (!t) return null
                if (typeof t === 'string') return <option key={t} value={t}>{t}</option>
                return <option key={t.value ?? t.id} value={t.value ?? t.id}>{t.label ?? t.label ?? t.value ?? t.id}</option>
              })}
            </select>
          </div>
        ) : (
          <div>
            <div className="border rounded">
              <textarea value={message} onChange={e => setMessage(e.target.value)} className="w-full h-40 p-3 resize-none" placeholder="Compose your email here..." />
            </div>
          </div>
        )}

        <div className="mt-3">
          <label className="inline-flex items-center mr-4">
            <input type="checkbox" checked={schedule} onChange={() => setSchedule(s => !s)} className=" rounded-[3px] mr-2" />
            On Schedule Date And Time
          </label>
        </div>

        {schedule && (
          <div className="mt-2">
            <input type="datetime-local" value={scheduleAt} onChange={e => setScheduleAt(e.target.value)} className="w-full border px-3 py-2 rounded" />
          </div>
        )}

        {error && <div className="text-red-600 text-sm mt-2">{error}</div>}

      </div>
    </div>
  )
}

export default SendMail
